import { CreateClientConfirmationBtn } from '@AMIEWEB/Placement/CreateClientConfirmationDoc/CreateClientConfirmationBtn';
import { Grid, Skeleton, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import NoClientConfirmationDocs from 'app/assets/images/IconImages/NoClientConfirmationDocs.svg';
import { ConfirmationCard } from './ConfirmationCard';
import { useDispatch, useSelector } from 'react-redux';
import { placementDocumentsActions } from 'store/redux-store/placement-documents/slice';
import { selectPlacementHeaderData } from 'store/redux-store/placement-details/selectors';
import { DocStatus } from 'store/redux-store/placement-documents/types';
import { PlacementService } from 'app/services/PlacementServices/placement-service';
import { trackPromise } from 'react-promise-tracker';
import { selectUser } from 'oidc/user.selectors';
import { Concatenate } from 'utils/string/string';
import { globalActions } from 'app/ApplicationRoot/Global.redux';

const useStyles = makeStyles()(theme => ({
  container: {
    paddingTop: 24,
  },
  loadingSkeleton: {
    height: 116,
    transform: 'unset',
  },
  noContent: {
    padding: 48,
  },
  noContentMsg: {
    textAlign: 'center',
    fontStyle: 'italic',
    marginTop: 18,
  },
  title: {
    color: '#676767',
  },
  snackBarNoActionItems: {
    color: theme.palette.system.secondarySnack,
    '&:hover': {
      cursor: 'pointer',
    },
    fontWeight: 'bold',
  },
  snackBarYesActionItems: {
    color: theme.palette.framework.system.skyBlue,
    '&:hover': {
      cursor: 'pointer',
    },
    fontWeight: 'bold',
  },
}));

export enum ActionMethodType {
  create,
  delete,
}

export const ClientConfirmationDialogContent = ({
  clientConfirmationDocs = [],
  selectedConfirmationDoc,
  onDocSelection,
  loading,
  inlineDownload,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { placementId } = useSelector(selectPlacementHeaderData);
  const {
    userInfo: { employeeId: userId, firstName, lastName },
  } = useSelector(selectUser);

  const draftExists = React.useMemo(
    () => clientConfirmationDocs.some(doc => doc.status === DocStatus.Draft),
    [clientConfirmationDocs],
  );

  const docContainer = React.useMemo(
    () =>
      !!clientConfirmationDocs.length ? (
        clientConfirmationDocs.map(item => (
          <Grid item xs={6} key={item.documentId}>
            <ConfirmationCard
              docCard={item}
              onClick={onDocSelection}
              selected={item.documentId === selectedConfirmationDoc?.documentId}
              onDelete={() =>
                dispatch(
                  globalActions.setSnackBar({
                    message: 'Are you sure you want to discard this draft?',
                    disableIcon: true,
                    severity: null,
                    actions: [
                      {
                        action: () => handleYesAction(null, ActionMethodType.create),
                        text: t('Yes'),
                      },
                      {
                        closeSnackbar: true,
                        text: t('No'),
                      },
                    ],
                  }),
                )
              }
              inlineDownload={inlineDownload}
            />
          </Grid>
        ))
      ) : (
        <Grid container direction="column" classes={{ root: classes.noContent }} spacing={2}>
          <img src={NoClientConfirmationDocs} alt="NoDocumentsToView" />
          <Typography variant="body1" classes={{ root: classes.noContentMsg }}>
            {t('placement.profile.clientConfirmation.dialog.content.noDocsText')}
          </Typography>
        </Grid>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientConfirmationDocs, selectedConfirmationDoc, placementId],
  );

  const deleteDraftTemplate = React.useCallback(async () => {
    const placementService = PlacementService.createInstance();
    await placementService.deleteClientConfirmationTemplateDraft({
      placementId,
      userId,
      userName: Concatenate([firstName, lastName], ' '),
    });
  }, [firstName, lastName, placementId, userId]);

  const handleYesAction = async (callbackFn: any, actionType: ActionMethodType) => {
    if (actionType === ActionMethodType.create) {
      await trackPromise(deleteDraftTemplate(), 'delete-placement-draft-template');
      callbackFn();
    } else if (actionType === ActionMethodType.delete) {
      dispatch(placementDocumentsActions.deleteDraftTemplate({ placementId }));
    }
  };

  const handleCreateConfirmation = React.useCallback(
    callbackFn => {
      if (draftExists)
        dispatch(
          globalActions.setSnackBar({
            message: 'Are you sure you want to discard this draft?',
            disableIcon: true,
            severity: null,
            actions: [
              {
                action: () => handleYesAction(callbackFn, ActionMethodType.create),
                closeSnackbar: true,
                text: t('Yes'),
              },
              {
                closeSnackbar: true,
                text: t('No'),
              },
            ],
          }),
        );
      else callbackFn();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteDraftTemplate, draftExists],
  );

  return (
    <Grid container spacing={2} className={classes.container}>
      <Grid item xs={12}>
        <Grid container direction="row" justifyContent="space-between">
          <Grid item>
            <Typography variant="body1" classes={{ body1: classes.title }}>
              {t('placement.profile.clientConfirmation.dialog.content.initialInstructions')}
            </Typography>
          </Grid>
          <Grid item>
            <CreateClientConfirmationBtn onClickWithCallback={handleCreateConfirmation} />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        {loading ? (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Skeleton className={classes.loadingSkeleton} />
            </Grid>
            <Grid item xs={6}>
              <Skeleton className={classes.loadingSkeleton} />
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            {docContainer}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
